import React, { useEffect } from "react";
import { ThemeProvider } from "styled-components";

import Exception from "@/components/Exception";
import { FAVICON_CDN } from "@/config";
import { GlobalConsumer, GlobalContextProps, GlobalProvider } from "@/contexts";
import { useTheme } from "@/hooks";
import { LocaleProvider } from "@/i18n";
import IFrameService from "@/service/IFrame";
import { Interaction, InteractionResult } from "@/service/Model";
import ResizeService from "@/service/Resize";
import TrackingService from "@/service/Tracking";
import { GlobalStyles } from "@/styles";
import { ThemeName } from "@/styles/Theme";

const RESIZE_INTERVAL = 200;

export type ContainerProps = {
  interaction: Interaction;
  interactionResults: InteractionResult;
  onDestroy?: () => void;
};

const useDocumentMeta = (interaction: Interaction, theme: ThemeName) => {
  useEffect(() => {
    const { title, layout, type } = interaction;
    const link =
      (document.querySelector("link[rel*='icon']") as HTMLLinkElement) ||
      document.createElement("link");
    link.type = "image/x-icon";
    link.rel = "shortcut icon";
    const faviconPath = [type, layout, theme, "favicon.ico"]
      .filter(Boolean)
      .join("/");
    link.href = new URL(faviconPath, FAVICON_CDN).href;
    document.head.appendChild(link);
    document.title = title;
  }, [interaction, theme]);
};

const useServices = (interaction: Interaction) => {
  useEffect(() => {
    TrackingService.getInstance().interaction = interaction;
    IFrameService.initialized();
    IFrameService.getTealiumConfig();
    IFrameService.getCanonical();
    ResizeService.init(IFrameService.resize, RESIZE_INTERVAL);
  }, [interaction]);
};

export const AppWrapper: React.FC<{
  Container: React.ComponentType<ContainerProps>;
  onDestroy: () => void;
  query: IQuery;
}> = ({ Container, onDestroy, query }) => {
  return (
    <GlobalProvider query={query}>
      <Exception onDestroy={onDestroy}>
        <GlobalConsumer>
          {(context: GlobalContextProps) => {
            if (context.error) {
              throw context.error;
            }
            if (!context.response) {
              return <React.Fragment />;
            }
            if (!context.brandSettings) {
              throw new Error("Unable to detect brand.");
            }

            return (
              <AppContent
                context={context}
                Container={Container}
                onDestroy={onDestroy}
              />
            );
          }}
        </GlobalConsumer>
      </Exception>
    </GlobalProvider>
  );
};

const AppContent: React.FC<{
  context: GlobalContextProps;
  Container: React.ComponentType<ContainerProps>;
  onDestroy: () => void;
}> = ({ context, Container, onDestroy }) => {
  const { interaction, interactionResults } = context.response!;
  const { themeName, locale } = context.brandSettings;

  useDocumentMeta(interaction, themeName);
  useServices(interaction);
  const selectedTheme = useTheme(themeName, interaction);

  return (
    <LocaleProvider locale={locale}>
      <ThemeProvider theme={selectedTheme}>
        <GlobalStyles themeName={themeName} />
        <Container
          interaction={interaction}
          interactionResults={interactionResults}
          onDestroy={onDestroy}
        />
      </ThemeProvider>
    </LocaleProvider>
  );
};
